import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { applicationTypes, extentsApi, legendConfigApi } from '../stitch'

/**
 * One rendered map layer of one pass, as tailor stores it: the frame galileo rendered it in, the
 * layer's definition, which carries enough to label and legend it, and where its image is.
 *
 * A pass has one of these per layer of every application it recorded, so the same
 * `applicationType` and `layerKey` can appear more than once for a single stream: two granular
 * applications each carry their own tank mix.
 */
export const mapLayerRenderSchema = z.object({
  applicationId: z.string(),
  applicationType: applicationTypes,
  /**
   * The operation's name for the application, e.g. "Planter". Absent when the pass's application
   * list was not stored when its layers were rendered.
   */
  applicationName: z.string().optional(),
  layerId: z.string(),
  /**
   * The category of the data the layer holds, e.g. `coverage` or `dryyield`. Comparable across
   * streams, unlike `layerId`.
   */
  layerKey: z.string(),
  coordinates: extentsApi.responseSchema.shape.coordinates,
  legend: legendConfigApi.responseSchema,
  /**
   * The image's key in the map layer renders bucket.
   */
  renderKey: z.string(),
  /**
   * Which of galileo's renders of the layer this is. A re-render writes its image over the same
   * key, so the version is what tells one render's image from the next.
   */
  renderVersion: z.string()
})

export type MapLayerRender = z.infer<typeof mapLayerRenderSchema>

export enum LayerPaletteKind {
  Discrete = 'discrete',
  Continuous = 'continuous'
}

/**
 * A render is colored into an indexed PNG, whose 256 palette entries include one for transparency.
 */
const PALETTE_CAPACITY = 255

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/)

/**
 * The color for pixels that carry one discrete value, identified by the render's own blue value
 * for it.
 */
const blueColorSchema = z.object({
  blue: z.int().min(1).max(255),
  color: hexColorSchema
})

/**
 * The colors a report gives one map, reconciled with every other map of the same application and
 * layer. It carries only what the report chooses; how the render encodes its values is the
 * render's own.
 */
export const layerPaletteSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(LayerPaletteKind.Discrete),
    colors: z.array(blueColorSchema).max(PALETTE_CAPACITY)
  }),
  z
    .object({
      kind: z.literal(LayerPaletteKind.Continuous),
      /**
       * Where the first in-range bin begins and the last ends. Values beyond it take the bin at
       * that end.
       */
      range: z
        .object({ minimum: z.number(), maximum: z.number() })
        .refine((range) => range.maximum > range.minimum),
      /**
       * One color per bin, lowest first: a bin below the range, a bin above it, and at least one
       * within it.
       */
      binColors: z.array(hexColorSchema).min(3),
      /**
       * Values some continuous layers encode as blue alone, like down force's loss of ground
       * contact.
       */
      colors: z.array(blueColorSchema)
    })
    .refine((palette) => palette.binColors.length + palette.colors.length <= PALETTE_CAPACITY)
])

export type LayerPalette = z.infer<typeof layerPaletteSchema>

/**
 * A palette rides in every render url, so it is written compactly, in characters a query string
 * carries unescaped. Groups are separated by `*` and the values within one by `_`:
 *
 * - discrete: `d*<blue>_<color>*<blue>_<color>…`
 * - continuous: `c*<minimum>_<maximum>*<color>_<color>…*<blue>_<color>…`
 *
 * Colors are hex without their `#`.
 */
const GROUP_SEPARATOR = '*'
const VALUE_SEPARATOR = '_'

const PALETTE_KIND_CODES: Record<LayerPaletteKind, string> = {
  [LayerPaletteKind.Discrete]: 'd',
  [LayerPaletteKind.Continuous]: 'c'
}

const encodeBlueColor = ({ blue, color }: { blue: number; color: string }) =>
  [blue, color.slice(1)].join(VALUE_SEPARATOR)

const decodeBlueColor = (group: string) => {
  const [blue, color] = group.split(VALUE_SEPARATOR)
  return { blue: Number(blue), color: `#${color}` }
}

const decodePaletteGroups = ([code, ...groups]: string[]): unknown => {
  if (code === PALETTE_KIND_CODES[LayerPaletteKind.Discrete]) {
    return { kind: LayerPaletteKind.Discrete, colors: groups.map(decodeBlueColor) }
  }
  if (code !== PALETTE_KIND_CODES[LayerPaletteKind.Continuous]) return undefined
  const [range = '', bins = '', ...colors] = groups
  const [minimum, maximum] = range.split(VALUE_SEPARATOR).map(Number)
  return {
    kind: LayerPaletteKind.Continuous,
    range: { minimum, maximum },
    binColors: bins.split(VALUE_SEPARATOR).map((color) => `#${color}`),
    colors: colors.map(decodeBlueColor)
  }
}

export const compactPaletteCodec = z.codec(z.string(), layerPaletteSchema, {
  decode: (text, ctx) => {
    const palette = layerPaletteSchema.safeParse(decodePaletteGroups(text.split(GROUP_SEPARATOR)))
    if (palette.success) return palette.data
    ctx.issues.push({ code: 'custom', message: 'Not a palette', input: text })
    return z.NEVER
  },
  encode: (palette) => {
    const groups =
      palette.kind === LayerPaletteKind.Discrete
        ? palette.colors.map(encodeBlueColor)
        : [
            [palette.range.minimum, palette.range.maximum].join(VALUE_SEPARATOR),
            palette.binColors.map((color) => color.slice(1)).join(VALUE_SEPARATOR),
            ...palette.colors.map(encodeBlueColor)
          ]
    return [PALETTE_KIND_CODES[palette.kind], ...groups].join(GROUP_SEPARATOR)
  }
})

/**
 * One pass's rendered map layer as a PNG, colored so that it reads alongside the other maps of a
 * report. The path addresses the render the same way its row is keyed, and ends in the render's
 * version, so a url names exactly one image.
 */
export const mapLayerRenderApi = defineApi({
  name: 'GetMapLayerRender',
  description:
    "Retrieve one pass's rendered map layer as a PNG image, colored by a report's reconciled legend.",
  method: 'GET',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  pathSchema: z.object({
    orgCode: z.string(),
    streamId: z.string(),
    applicationType: applicationTypes,
    layerKey: z.string(),
    applicationId: z.string(),
    layerId: z.string(),
    renderVersion: z.string()
  }),
  querySchema: z.object({ palette: compactPaletteCodec }),
  pathFor: (p) =>
    path(
      `/tailor/${p.orgCode}/map-layer-renders/${p.streamId}/${p.applicationType}/${p.layerKey}/${p.applicationId}/${p.layerId}/${p.renderVersion}`,
      { palette: z.encode(compactPaletteCodec, p.palette) }
    ),
  gatewayPath:
    '/:orgCode/map-layer-renders/:streamId/:applicationType/:layerKey/:applicationId/:layerId/:renderVersion' as const,
  responseSchema: z.instanceof(Uint8Array)
})
